import React, { useImperativeHandle, forwardRef, useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {ApiGridSearch} from '@assets/js/api/commonApi.js';

const CompGridMatchmaking = forwardRef((props, ref) => {
    // Forward ref to allow parent components to call event functions.
    // This is useful for controlling the modal from outside this component.
    useImperativeHandle(ref, () => ({
        onGridSearch: (p_params) => {
            onGridSearch(p_params);
        },
    }));

    /**********************
     * Define Grid vaiable 
    ***********************/
    // State for grid pagination and loading
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 100,
    });
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [totalRowCount, setTotalRowCount] = useState(0);
    const [rowSelectionModel, setRowSelectionModel] = useState({ type: 'include', ids: new Set() });

    // Define columns for DataGrid
    const columns = [
        { field: 'rowNum', headerName: 'No.', width: 65, headerAlign: 'center', align: 'center' },
        { field: 'comptnNm', headerName: '대회명', width: 200, headerAlign: 'center', align: 'left' },
        { field: 'mtmkgTycdNm', headerName: '대진타입', width: 100, headerAlign: 'center', align: 'center' },
        { field: 'mtmkgStatNm', headerName: '대진상태', width: 100, headerAlign: 'center', align: 'center' },
        { field: 'mtmkgNm', headerName: '대진명', width: 150, headerAlign: 'center', align: 'left', 
            renderCell: (params) => (
                <span style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }} onClick={() => handleRowClick(params)}>
                    {params.value}
                </span>
            )
        },
        { field: 'dvsnNm', headerName: '부문명', width: 150, headerAlign: 'center', align: 'left' },
        { field: 'wetclsNm', headerName: '체급명', width: 100, headerAlign: 'center', align: 'center' },
        { field: 'trmntCnt', headerName: '토너먼트 생성', width: 100, headerAlign: 'center', align: 'center',
            renderCell: (params) => (
                <span style={params.value > 0 ? { color: 'white', background: 'green' } : {}}>
                    {params.value > 0 ? '완료' :  '미완료'}
                </span>
            )
        },
        { field: 'plyrLCnt', headerName: '왼팔', width: 100, headerAlign: 'center', align: 'center',
            renderCell: (params) => {
                if (params.value >= 3 && params.value <= 32) {
                    return (
                        <span style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }} onClick={() => handleRowClick(params)}>
                            {params.value + '명'}
                        </span>
                    );
                }
                return <span></span>;
            }
        },
        { field: 'plyrRCnt', headerName: '오른팔', width: 100, headerAlign: 'center', align: 'center',
            renderCell: (params) => {
                if (params.value >= 3 && params.value <= 32) {
                    return (
                        <span style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }} onClick={() => handleRowClick(params)}>
                            {params.value + '명'}
                        </span>
                    );
                }
                return <span></span>;
            }
        },
        { field: 'useYnNm', headerName: '사용여부', width: 90, headerAlign: 'center', align: 'center' },
    ];

    /*********************
     * Define Grid events
    ***********************/
    const onGridSearch = (p_params) => {
        let params = {
            page: paginationModel.page,
            pageSize: paginationModel.pageSize,
            offset: paginationModel.page*paginationModel.pageSize,
            ...p_params
        }

        // <-- loading start
        setLoading(true); 
        // Fetch data from the server
        ApiGridSearch('/sys/mtmkgmng/search/list', params)
        .then(res => {
            if (res.data.result) {
                const rows = res.data.dataList.length > 0 ? res.data.dataList : [];
                const totalCount = res.data.dataList.length > 0 ? res.data.dataList[0].totalCount : 0;
                setRows(rows);
                setTotalRowCount(totalCount);
            }
            setRowSelectionModel({ type: 'include', ids: new Set() });
        })
        .catch(error => {
            console.error('Error fetching matchmaking list:', error);
        })
        .finally(() => {
            // <-- loading end
            setLoading(false); 
        });
    }

    // Pass selected row data to parent component
    const handleRowClick = (params) => {
        if (params.field === 'mtmkgNm' || params.field === 'plyrLCnt' || params.field === 'plyrRCnt') {
            props.onChildClick(params.row, params.field); 
        }
    };

    /**********************
     * Define useEffect
    ***********************/
    useEffect(() => {
        onGridSearch();
        // eslint-disable-next-line
    }, [paginationModel]);

    return (
        <>
            <div className="grid-container">
                <h4>대진 목록</h4>
            </div>
            <div style={{ height: 520, width: '100%', minHeight: 400 }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowId={(row) => `${row.mtmkgCd}`}
                    loading={loading}
                    pageSizeOptions={[100, 200, 500]}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    paginationMode="server"
                    rowCount={totalRowCount}
                    checkboxSelection
                    rowSelectionModel={rowSelectionModel}
                    onRowSelectionModelChange={(newSelection) => {
                        const arraySelections = [...newSelection.ids];
                        if (props.onSelectionChange) {
                            const selectedRowData = arraySelections.map(id => {
                                return rows.find(row => id === `${row.mtmkgCd}`);
                            });

                            setRowSelectionModel(newSelection);
                            props.onSelectionChange({ ids: newSelection.ids, data: selectedRowData });
                        }
                    }}
                    keepNonExistentRowsSelected
                /> 
            </div>
        </> 
    );
});

export default CompGridMatchmaking;